<template>
  <div class="gl-transfers-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Transfers</span>
          </div>
          <h1 class="gl-branch-title">Stock Transfers</h1>
        </div>
        <router-link to="/owner/transfers/create" class="gl-add-btn gl-header-add-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Transfer
        </router-link>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip (clickable filters) -->
      <div class="gl-stats-strip">
        <div
          class="gl-stat-chip gl-stat-clickable"
          :class="{ 'gl-stat-chip-active gl-stat-chip-warn': filters.status === 'pending' }"
          @click="filterByStatus('pending')"
        >
          <span class="gl-stat-num gl-stat-warn">{{ counts.pending }}</span>
          <span class="gl-stat-lbl">Pending Approval</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div
          class="gl-stat-chip gl-stat-clickable"
          :class="{ 'gl-stat-chip-active gl-stat-chip-blue': filters.status === 'approved' }"
          @click="filterByStatus('approved')"
        >
          <span class="gl-stat-num gl-stat-info">{{ counts.approved }}</span>
          <span class="gl-stat-lbl">Approved</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div
          class="gl-stat-chip gl-stat-clickable"
          :class="{ 'gl-stat-chip-active gl-stat-chip-teal': filters.status === 'in_transit' }"
          @click="filterByStatus('in_transit')"
        >
          <span class="gl-stat-num" style="color:#900C27">{{ counts.in_transit }}</span>
          <span class="gl-stat-lbl">In Transit</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div
          class="gl-stat-chip gl-stat-clickable"
          :class="{ 'gl-stat-chip-active gl-stat-chip-gray': filters.status === '' }"
          @click="filterByStatus('')"
        >
          <span class="gl-stat-num">{{ pagination.total }}</span>
          <span class="gl-stat-lbl">All Transfers</span>
        </div>
        <div class="gl-stat-spacer"></div>
        <div class="gl-filter-select-wrap" v-if="filters.status !== ''">
          <select class="gl-filter-select-inline" v-model="filters.status" @change="fetchTransfers">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="in_transit">In Transit</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="gl-filter-bar">
        <select class="gl-filter-select" v-model="filters.status" @change="fetchTransfers">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="in_transit">In Transit</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button class="gl-filter-clear" @click="filterByStatus('')" v-if="filters.status">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Clear
        </button>
      </div>

      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-table-card">
          <div class="gl-skel-table">
            <div v-for="i in 7" :key="i" class="gl-skel-row" :style="`animation-delay:${i*30}ms`">
              <div class="gl-skel-line w30"></div>
              <div class="gl-skel-line w20"></div>
              <div class="gl-skel-line w20"></div>
              <div class="gl-skel-line w10"></div>
              <div class="gl-skel-badge"></div>
              <div class="gl-skel-line w20"></div>
              <div class="gl-skel-actions">
                <div class="gl-skel-line gl-skel-btn-sq"></div>
                <div class="gl-skel-line gl-skel-btn-sq"></div>
                <div class="gl-skel-line gl-skel-btn-sq"></div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Table -->
      <div v-else class="gl-table-card">

        <div v-if="!transfers.length" class="gl-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
          <p>No transfers found.</p>
          <router-link to="/owner/transfers/create" class="gl-add-btn" style="font-size:13px;padding:8px 16px">Create first transfer</router-link>
        </div>

        <div v-else class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th>Transfer #</th>
                <th>From</th>
                <th>To</th>
                <th class="th-center">Items</th>
                <th>Status</th>
                <th>Created</th>
                <th class="th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="transfer in transfers" :key="transfer.id">
                <td>
                  <router-link :to="`/owner/transfers/${transfer.id}`" class="gl-transfer-num">
                    {{ transfer.transfer_number }}
                  </router-link>
                </td>
                <td>
                  <span class="gl-outlet-name">{{ transfer.source_outlet?.name || '—' }}</span>
                </td>
                <td>
                  <span class="gl-outlet-name">{{ transfer.destination_outlet?.name || '—' }}</span>
                </td>
                <td class="td-center">
                  <span class="gl-items-pill">{{ transfer.items?.length || 0 }}</span>
                </td>
                <td>
                  <span class="gl-status-badge" :class="`gl-status-${transfer.status}`">
                    {{ formatStatus(transfer.status) }}
                  </span>
                </td>
                <td>
                  <div class="gl-created-date">{{ formatDate(transfer.created_at) }}</div>
                  <div class="gl-created-by" v-if="transfer.creator?.name">by {{ transfer.creator.name }}</div>
                </td>
                <td class="td-center">
                  <div class="gl-action-group">
                    <router-link :to="`/owner/transfers/${transfer.id}`" class="gl-act-btn gl-act-view" title="View details">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </router-link>
                    <button
                      v-if="transfer.status === 'pending'"
                      class="gl-act-btn gl-act-approve"
                      @click="approveTransfer(transfer)"
                      :disabled="processing === transfer.id"
                      title="Approve"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </button>
                    <button
                      v-if="transfer.status === 'pending' || transfer.status === 'approved'"
                      class="gl-act-btn gl-act-cancel"
                      @click="showCancelModal(transfer)"
                      title="Cancel"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="gl-pagination">
          <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchTransfers(pagination.current_page - 1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            v-for="page in pagination.last_page"
            :key="page"
            class="gl-page-btn"
            :class="{ active: page === pagination.current_page }"
            @click="fetchTransfers(page)"
          >{{ page }}</button>
          <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchTransfers(pagination.current_page + 1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </div>

    </div>

    <!-- Mobile FAB — New Transfer -->
    <router-link to="/owner/transfers/create" class="gl-mobile-fab">
      <span class="gl-fab-inner">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        New Transfer
      </span>
    </router-link>

    <!-- ── CANCEL MODAL ── -->
    <div v-if="cancelModalOpen" class="gl-modal-backdrop" @click.self="cancelModalOpen = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <div class="gl-modal-title-wrap">
            <div class="gl-modal-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
            <span class="gl-modal-title">Cancel Transfer</span>
          </div>
          <button class="gl-modal-close" @click="cancelModalOpen = false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-info-row">
            <div class="gl-info-label">Transfer</div>
            <div class="gl-info-value">{{ selectedTransfer?.transfer_number }}</div>
          </div>
          <div class="gl-form-group">
            <label class="gl-label">Reason <span class="gl-required">*</span></label>
            <textarea class="gl-textarea" v-model="cancelReason" rows="3" placeholder="Provide a reason for cancellation…"></textarea>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-secondary" @click="cancelModalOpen = false">Keep Transfer</button>
          <button class="gl-btn-danger" @click="cancelTransfer" :disabled="!cancelReason || processing">
            <span v-if="processing" class="gl-btn-spinner"></span>
            Cancel Transfer
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ownerTransferApi } from '@/services/ownerApi';

export default {
  name: 'TransferList',

  data() {
    return {
      loading: true,
      processing: null,
      transfers: [],
      selectedTransfer: null,
      cancelReason: '',
      cancelModalOpen: false,
      filters: {
        status: ''
      },
      counts: {
        pending: 0,
        approved: 0,
        in_transit: 0
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      }
    };
  },

  mounted() {
    this.fetchTransfers();
  },

  methods: {
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    formatStatus(status) {
      const statuses = {
        pending: 'Pending', approved: 'Approved',
        in_transit: 'In Transit', received: 'Received', cancelled: 'Cancelled'
      };
      return statuses[status] || status;
    },

    filterByStatus(status) {
      this.filters.status = status;
      this.fetchTransfers();
    },

    async fetchTransfers(page = 1) {
      try {
        this.loading = true;
        const response = await ownerTransferApi.list({ page, ...this.filters });
        this.transfers = response.data.data;
        this.counts = response.data.counts || this.counts;
        if (response.data.meta) {
          this.pagination = response.data.meta;
        }
      } catch (error) {
        console.error('Failed to fetch transfers:', error);
      } finally {
        this.loading = false;
      }
    },

    async approveTransfer(transfer) {
      if (!confirm(`Approve transfer ${transfer.transfer_number}?`)) return;
      try {
        this.processing = transfer.id;
        await ownerTransferApi.approve(transfer.id);
        this.fetchTransfers(this.pagination.current_page);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to approve transfer');
      } finally {
        this.processing = null;
      }
    },

    showCancelModal(transfer) {
      this.selectedTransfer = transfer;
      this.cancelReason = '';
      this.cancelModalOpen = true;
    },

    async cancelTransfer() {
      if (!this.cancelReason) return;
      try {
        this.processing = true;
        await ownerTransferApi.cancel(this.selectedTransfer.id, this.cancelReason);
        this.cancelModalOpen = false;
        this.fetchTransfers(this.pagination.current_page);
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to cancel transfer');
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page layout ── */
.gl-branch-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-branch-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }

.gl-add-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; background: #900C27; color: #fff;
  border: none; border-radius: 10px; font-size: 13.5px; font-weight: 600;
  cursor: pointer; text-decoration: none;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
}
.gl-add-btn:hover { background: #6f0a1e; color: #fff; text-decoration: none; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(144,12,39,0.30); }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 8px 14px; margin-bottom: 14px; flex-wrap: wrap; gap: 2px;
}
.gl-stat-chip { display: flex; flex-direction: column; align-items: flex-start; padding: 6px 14px; border-radius: 9px; }
.gl-stat-num { font-size: 20px; font-weight: 700; color: #111827; line-height: 1.1; }
.gl-stat-lbl { font-size: 11px; color: #9ca3af; font-weight: 500; margin-top: 1px; white-space: nowrap; }
.gl-stat-divider { width: 1px; height: 32px; background: #f0f1f3; margin: 0 4px; flex-shrink: 0; }
.gl-stat-spacer { flex: 1; }
.gl-stat-warn { color: #d97706 !important; }
.gl-stat-info { color: #2563eb !important; }

/* Clickable chips */
.gl-stat-clickable { cursor: pointer; transition: background 0.15s, box-shadow 0.15s; }
.gl-stat-clickable:hover { background: #f9fafb; }
.gl-stat-chip-active { outline: 1.5px solid transparent; }
.gl-stat-chip-active.gl-stat-chip-warn { background: #fffbf0; outline-color: #fde68a; }
.gl-stat-chip-active.gl-stat-chip-blue { background: #eff6ff; outline-color: #bfdbfe; }
.gl-stat-chip-active.gl-stat-chip-teal { background: #f0f5f4; outline-color: #a7c4be; }
.gl-stat-chip-active.gl-stat-chip-gray { background: #f9fafb; outline-color: #e5e7eb; }

/* Inline filter in stats strip */
.gl-filter-select-wrap { display: flex; align-items: center; }
.gl-filter-select-inline { border: 1.5px solid #e8eaf0; border-radius: 8px; padding: 5px 10px; font-size: 12.5px; color: #374151; background: #fff; outline: none; cursor: pointer; }
.gl-filter-select-inline:focus { border-color: #900C27; }

/* ── Filter bar ── */
.gl-filter-bar {
  display: flex; align-items: center; gap: 10px;
  background: transparent; border: none;
  padding: 0; margin-bottom: 16px;
}
.gl-filter-select { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 12px; font-size: 13px; color: #374151; background: #fff; outline: none; cursor: pointer; transition: border-color 0.15s; }
.gl-filter-select:focus { border-color: #900C27; }
.gl-filter-clear { display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; border: 1.5px solid #fecaca; border-radius: 8px; background: #fff5f5; color: #dc2626; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
.gl-filter-clear:hover { background: #fee2e2; }

/* ── Skeleton ── */
.gl-skel-table { padding: 8px 0; }
.gl-skel-row { display: flex; align-items: center; gap: 16px; padding: 14px 16px; border-bottom: 1px solid #f5f6f8; animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-skel-row:last-child { border-bottom: none; }
@keyframes gl-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
.gl-skel-line { height: 12px; background: #f0f1f3; border-radius: 6px; flex-shrink: 0; }
.w10 { width: 60px; } .w20 { width: 120px; } .w30 { width: 160px; }
.gl-skel-badge { width: 70px; height: 22px; background: #f0f1f3; border-radius: 20px; flex-shrink: 0; }
.gl-skel-actions { display: flex; gap: 4px; margin-left: auto; flex-shrink: 0; }
.gl-skel-btn-sq { width: 30px; height: 30px; background: #f0f1f3; border-radius: 7px; flex-shrink: 0; }

/* ── Table card ── */
.gl-table-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px; overflow: hidden; margin-bottom: 24px; }
.gl-table-wrap { overflow-x: auto; }
.gl-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.gl-table thead tr { background: #f8f9fa; border-bottom: 1.5px solid #f0f1f3; }
.gl-table thead th { padding: 11px 16px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; white-space: nowrap; }
.gl-table thead th.th-center { text-align: center; }
.gl-table tbody tr { border-bottom: 1px solid #f5f6f8; transition: background 0.1s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #fafafa; }
.gl-table tbody td { padding: 12px 16px; color: #374151; vertical-align: middle; }
.gl-table tbody td.td-center { text-align: center; }

/* Transfer number link */
.gl-transfer-num { font-size: 13px; font-weight: 700; color: #900C27; text-decoration: none; font-family: 'Courier New', monospace; letter-spacing: 0.02em; }
.gl-transfer-num:hover { text-decoration: underline; color: #6f0a1e; }

/* Outlet name */
.gl-outlet-name { font-size: 13.5px; color: #374151; }

/* Items pill */
.gl-items-pill { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 22px; padding: 0 8px; background: #f3f4f6; color: #6b7280; font-size: 12px; font-weight: 600; border-radius: 20px; }

/* Status badge */
.gl-status-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.gl-status-pending    { background: #fef3c7; color: #d97706; }
.gl-status-approved   { background: #dbeafe; color: #2563eb; }
.gl-status-in_transit { background: #dff0ec; color: #900C27; }
.gl-status-received   { background: #dcfce7; color: #16a34a; }
.gl-status-cancelled  { background: #fee2e2; color: #dc2626; }

/* Created date */
.gl-created-date { font-size: 13px; color: #374151; }
.gl-created-by { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }

/* Action buttons */
.gl-action-group { display: flex; align-items: center; gap: 4px; justify-content: center; }
.gl-act-btn { width: 30px; height: 30px; border-radius: 7px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: opacity 0.15s, transform 0.1s, background 0.15s; text-decoration: none; flex-shrink: 0; }
.gl-act-btn:hover { transform: scale(1.07); text-decoration: none; }
.gl-act-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
.gl-act-view    { border: 1.5px solid #e8eaf0; background: #fff; color: #6b7280; }
.gl-act-view:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-act-approve { border: none; background: #900C27; color: #fff; }
.gl-act-approve:hover:not(:disabled) { opacity: 0.85; }
.gl-act-cancel  { border: 1.5px solid #fecaca; background: #fff5f5; color: #dc2626; }
.gl-act-cancel:hover { background: #fee2e2; border-color: #fca5a5; }

/* Empty */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 80px 0; color: #9ca3af; text-align: center; }
.gl-empty p { margin: 0; font-size: 14px; }

/* Pagination */
.gl-pagination { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 14px; border-top: 1px solid #f0f1f3; }
.gl-page-btn { min-width: 32px; height: 32px; padding: 0 8px; border-radius: 7px; border: 1.5px solid #e8eaf0; background: #fff; font-size: 13px; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.12s; }
.gl-page-btn:hover:not(:disabled) { border-color: #a7c4be; background: #f0f5f4; }
.gl-page-btn.active { background: #900C27; border-color: #900C27; color: #fff; font-weight: 700; }
.gl-page-btn:disabled { opacity: 0.38; cursor: not-allowed; }

/* ── Modal ── */
.gl-modal-backdrop { position: fixed; inset: 0; background: rgba(17,24,39,0.5); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 24px; }
.gl-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 440px; box-shadow: 0 20px 60px rgba(0,0,0,0.18); }
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 22px 0; }
.gl-modal-title-wrap { display: flex; align-items: center; gap: 10px; }
.gl-modal-icon { width: 30px; height: 30px; border-radius: 8px; background: #fee2e2; color: #dc2626; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-modal-title { font-size: 15px; font-weight: 700; color: #111827; }
.gl-modal-close { width: 28px; height: 28px; border-radius: 7px; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
.gl-modal-close:hover { background: #fee2e2; color: #dc2626; }
.gl-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
.gl-modal-footer { padding: 0 22px 20px; display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #f5f6f8; padding-top: 16px; margin-top: 2px; }

.gl-info-row { background: #f8f9fb; border: 1.5px solid #f0f1f3; border-radius: 9px; padding: 11px 14px; }
.gl-info-label { font-size: 11px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
.gl-info-value { font-size: 14px; font-weight: 700; color: #111827; font-family: 'Courier New', monospace; }

.gl-form-group { display: flex; flex-direction: column; gap: 5px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #374151; }
.gl-required { color: #dc2626; margin-left: 2px; }
.gl-textarea { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 8px 12px; font-size: 13.5px; color: #111827; outline: none; background: #fff; width: 100%; box-sizing: border-box; resize: vertical; transition: border-color 0.15s; }
.gl-textarea:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.1); }

.gl-btn-secondary { padding: 8px 18px; border-radius: 9px; border: 1.5px solid #e8eaf0; background: #fff; color: #6b7280; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.gl-btn-secondary:hover { background: #f3f4f6; }
.gl-btn-danger { padding: 8px 18px; border-radius: 9px; border: none; background: #dc2626; color: #fff; font-size: 13.5px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity 0.15s; }
.gl-btn-danger:hover:not(:disabled) { opacity: 0.88; }
.gl-btn-danger:disabled { opacity: 0.45; cursor: not-allowed; }
.gl-btn-spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin 0.7s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Mobile FAB ── */
.gl-mobile-fab {
  display: none; position: fixed; bottom: 24px; right: 20px; z-index: 200;
  background: transparent; border: none; text-decoration: none;
}
.gl-fab-inner {
  display: flex; align-items: center; gap: 8px;
  background: #900C27; color: #fff;
  padding: 13px 20px; border-radius: 50px;
  font-size: 14px; font-weight: 700;
  box-shadow: 0 6px 24px rgba(144,12,39,0.32), 0 2px 6px rgba(0,0,0,0.18);
  transition: transform 0.15s, box-shadow 0.15s;
}
.gl-mobile-fab:hover .gl-fab-inner { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(144,12,39,0.38); }

@media (max-width: 767px) {
  .gl-transfers-page { padding-bottom: 96px; }
  .gl-mobile-fab { display: block; }
  .gl-header-add-btn { display: none !important; }
  .gl-branch-title { font-size: 18px; }

  /* Stats strip — horizontal scroll chips */
  .gl-stats-strip {
    background: transparent; border: none; padding: 0;
    overflow-x: auto; flex-wrap: nowrap; gap: 8px;
    scrollbar-width: none; margin-bottom: 12px;
  }
  .gl-stats-strip::-webkit-scrollbar { display: none; }
  .gl-stat-chip { flex-shrink: 0; flex-direction: row; align-items: center; gap: 7px; padding: 7px 13px; background: #fff; border: 1.5px solid #f0f1f3; border-radius: 50px; white-space: nowrap; }
  .gl-stat-num { font-size: 14px; }
  .gl-stat-lbl { font-size: 12px; margin-top: 0; }
  .gl-stat-divider { display: none; }
  .gl-stat-spacer { display: none; }
  .gl-filter-select-wrap { flex-shrink: 0; }

  /* Filter bar */
  .gl-filter-bar { gap: 8px; }
  .gl-filter-select { width: 100%; flex: 1; }

  /* Table — hide Items (col 4) + Created (col 6) */
  .gl-table thead th:nth-child(4),
  .gl-table tbody td:nth-child(4),
  .gl-table thead th:nth-child(6),
  .gl-table tbody td:nth-child(6) { display: none; }

  .gl-table thead th,
  .gl-table tbody td { padding: 10px 10px; }
  .gl-act-btn { width: 34px; height: 34px; border-radius: 8px; }

  /* Modal */
  .gl-modal-backdrop { padding: 68px 16px 16px !important; align-items: center !important; }
  .gl-modal { border-radius: 16px !important; max-height: calc(100vh - 84px) !important; }
}
</style>

<style>
/* Dark mode */
body.dark-only .gl-branch-title { color: #f3f4f6; }
body.dark-only .gl-branch-breadcrumb { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #d1d5db; }

body.dark-only .gl-stats-strip { background: #262932; border-color: #323644; }
body.dark-only .gl-stat-num { color: #f3f4f6; }
body.dark-only .gl-stat-divider { background: #323644; }
body.dark-only .gl-stat-clickable:hover { background: #2d3249; }
body.dark-only .gl-stat-chip-active.gl-stat-chip-warn { background: rgba(217,119,6,0.1); outline-color: rgba(217,119,6,0.3); }
body.dark-only .gl-stat-chip-active.gl-stat-chip-blue { background: rgba(37,99,235,0.1); outline-color: rgba(37,99,235,0.3); }
body.dark-only .gl-stat-chip-active.gl-stat-chip-teal { background: rgba(144,12,39,0.1); outline-color: rgba(144,12,39,0.3); }
body.dark-only .gl-stat-chip-active.gl-stat-chip-gray { background: #2d3249; outline-color: #3d4460; }
body.dark-only .gl-filter-select-inline { background: #262932; border-color: #2d3249; color: #e5e7eb; }

body.dark-only .gl-filter-select { background: #262932; border-color: #2d3249; color: #e5e7eb; }

body.dark-only .gl-skel-row { border-color: #2a2f46; }
body.dark-only .gl-skel-line, body.dark-only .gl-skel-badge, body.dark-only .gl-skel-btn-sq { background: #2d3249; }

body.dark-only .gl-table-card { background: #1e2130; border-color: #2d3249; }
body.dark-only .gl-table thead tr { background: #252837; }
body.dark-only .gl-table thead th { color: #6b7280; }
body.dark-only .gl-table tbody tr { border-color: #2a2f46; }
body.dark-only .gl-table tbody tr:hover { background: #252837; }
body.dark-only .gl-table tbody td { color: #e5e7eb; }
body.dark-only .gl-outlet-name { color: #d1d5db; }
body.dark-only .gl-items-pill { background: #2d3249; color: #9ca3af; }
body.dark-only .gl-created-date { color: #d1d5db; }
body.dark-only .gl-act-view { border-color: #2d3249; background: #1e2130; color: #9ca3af; }
body.dark-only .gl-act-view:hover { border-color: #900C27; color: #4ade80; background: rgba(144,12,39,0.1); }
body.dark-only .gl-act-cancel { background: rgba(220,38,38,0.08); border-color: rgba(220,38,38,0.25); }

body.dark-only .gl-pagination { border-color: #2d3249; }
body.dark-only .gl-page-btn { border-color: #2d3249; background: #1e2130; color: #d1d5db; }
body.dark-only .gl-page-btn:hover:not(:disabled) { background: rgba(144,12,39,0.1); border-color: #900C27; }

body.dark-only .gl-modal { background: #1e2130; }
body.dark-only .gl-modal-title { color: #f3f4f6; }
body.dark-only .gl-modal-close { background: #2d3249; color: #9ca3af; }
body.dark-only .gl-modal-footer { border-color: #2d3249; }
body.dark-only .gl-info-row { background: #262932; border-color: #2d3249; }
body.dark-only .gl-info-value { color: #f3f4f6; }
body.dark-only .gl-label { color: #d1d5db; }
body.dark-only .gl-textarea { border-color: #2d3249; background: #262932; color: #e5e7eb; }
body.dark-only .gl-btn-secondary { border-color: #2d3249; background: #262932; color: #9ca3af; }

/* FAB */
.gl-mobile-fab { background: transparent !important; box-shadow: none !important; }
body.dark-only .gl-fab-inner { box-shadow: 0 6px 24px rgba(144,12,39,0.35), 0 2px 6px rgba(0,0,0,0.30) !important; }

/* Mobile dark stats chips */
@media (max-width: 767px) {
  body.dark-only .gl-stat-chip { background: #262932; border-color: #323644; }
  body.dark-only .gl-stat-chip-active.gl-stat-chip-warn { background: rgba(217,119,6,0.15); border-color: rgba(217,119,6,0.35); }
  body.dark-only .gl-stat-chip-active.gl-stat-chip-blue { background: rgba(37,99,235,0.15); border-color: rgba(37,99,235,0.35); }
  body.dark-only .gl-stat-chip-active.gl-stat-chip-teal { background: rgba(144,12,39,0.15); border-color: rgba(144,12,39,0.35); }
  body.dark-only .gl-stat-chip-active.gl-stat-chip-gray { background: #2d3249; border-color: #3d4460; }
}
</style>
